import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  Checkbox,
  EmptyState,
  Input,
  PageHeader,
  Skeleton,
  Tooltip,
  useToast,
} from '@provenance/design';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, FileCheck, Info, Plus, Save, Stamp, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../lib/api';

interface Rule {
  id: string;
  category: string;
  categoryLabel: string;
  required: boolean;
  stampByDefault: boolean;
  note: string | null;
  sortOrder: number;
}

interface TypeRules {
  documentTypeId: string;
  code: string;
  name: string;
  rules: Rule[];
}

/** A rule being edited. Identified by category, which is unique within a certificate type. */
interface Draft {
  category: string;
  required: boolean;
  stampByDefault: boolean;
  note: string;
}

/**
 * What documents a chamber asks for alongside each certificate type.
 *
 * The editor opens **inside the row it belongs to**. An earlier version rendered it below the
 * whole list, so clicking Configure on the third of fifteen types scrolled the thing you were
 * configuring off the screen and put the editor somewhere with no visible connection to it.
 *
 * Wording matters here more than usual. This screen used to say a type with nothing set had
 * "no opinion", which is the code's idea of the state and not a chamber's: the chamber is the
 * only party with an opinion, so being told they have none reads as a fault rather than a
 * setting. It now says what is true — nothing is requested — and offers the fix in the same
 * breath.
 */
export function RequiredDocuments() {
  const queryClient = useQueryClient();
  const [openType, setOpenType] = useState<string | null>(null);

  const types = useQuery({
    queryKey: ['required-documents'],
    queryFn: () => api.get<TypeRules[]>('/api/console/configuration/required-documents'),
  });
  const categories = useQuery({
    queryKey: ['supporting-categories'],
    queryFn: () => api.get<Array<{ code: string; label: string }>>('/api/console/documents/supporting-categories'),
  });

  const rows = types.data ?? [];
  const configured = rows.filter((t) => t.rules.length > 0);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Required documents"
        lede="Set per certificate type. Exporters see the list before they send, so the invoice and packing list arrive with the certificate instead of costing you a clarification two days later."
      />

      <Callout tone="info" icon={<Info className="size-4" />} title="Asking is not requiring.">
        A missing document is flagged to you and to the exporter — it never stops a request being
        lodged. Someone with a genuine reason to send without a packing list still can, and you can
        always ask for it afterwards.
      </Callout>

      {types.isLoading ? (
        <Skeleton className="h-64" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<FileCheck className="size-5" />}
          title="No certificate types yet"
          description="Once your chamber is accredited for a certificate type it will appear here."
        />
      ) : (
        <Card padded={false}>
          <div className="px-5 pt-5">
            <CardHeader
              title="Certificate types"
              description={
                configured.length === 0
                  ? 'None of these ask for anything yet. Open one and add the documents you normally need.'
                  : `${configured.length} of ${rows.length} ask for documents. Open a type to change what it asks for.`
              }
            />
          </div>
          <ul className="flex list-none flex-col gap-0 border-t border-[var(--pv-line)] p-0">
            {rows.map((type) => {
              const open = openType === type.documentTypeId;
              return (
                <li key={type.documentTypeId} className="border-b border-[var(--pv-line)] last:border-b-0">
                  <div className="flex flex-wrap items-center gap-3 px-5 py-3.5">
                    <div className="min-w-0 flex-1">
                      <p className="[font-size:13.5px] font-medium">{type.name}</p>
                      <p className="[font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
                        {type.rules.length === 0
                          ? 'No documents requested'
                          : type.rules
                              .map(
                                (r) =>
                                  `${r.categoryLabel}${r.required ? '' : ' (optional)'}${r.stampByDefault ? ' + stamp' : ''}`,
                              )
                              .join(' · ')}
                      </p>
                    </div>

                    {type.rules.some((r) => r.stampByDefault) && (
                      <Tooltip content="At least one of these is normally stamped as well as the certificate">
                        <Badge tone="leaf" size="sm">
                          <Stamp aria-hidden className="size-3" /> Stamped
                        </Badge>
                      </Tooltip>
                    )}
                    <Badge tone={type.rules.length === 0 ? 'neutral' : 'ok'} size="sm">
                      {type.rules.length === 0
                        ? 'None requested'
                        : `${type.rules.filter((r) => r.required).length} required`}
                    </Badge>

                    <Button
                      variant="secondary"
                      size="sm"
                      aria-expanded={open}
                      aria-controls={`rules-${type.documentTypeId}`}
                      leadingIcon={
                        open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />
                      }
                      onClick={() => setOpenType(open ? null : type.documentTypeId)}
                    >
                      {open ? 'Close' : type.rules.length === 0 ? 'Add documents' : 'Change'}
                    </Button>
                  </div>

                  {/* In the row, not at the foot of the page. */}
                  {open && (
                    <div
                      id={`rules-${type.documentTypeId}`}
                      className="border-t border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] px-5 py-4"
                    >
                      <RuleEditor
                        key={type.documentTypeId}
                        type={type}
                        categories={categories.data ?? []}
                        onSaved={() => {
                          void queryClient.invalidateQueries({ queryKey: ['required-documents'] });
                          setOpenType(null);
                        }}
                        onCancel={() => setOpenType(null)}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}

function RuleEditor({
  type,
  categories,
  onSaved,
  onCancel,
}: {
  type: TypeRules;
  categories: Array<{ code: string; label: string }>;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const toast = useToast();
  const [drafts, setDrafts] = useState<Draft[]>([]);

  useEffect(() => {
    setDrafts(
      type.rules.map((r) => ({
        category: r.category,
        required: r.required,
        stampByDefault: r.stampByDefault,
        note: r.note ?? '',
      })),
    );
  }, [type]);

  const save = useMutation({
    mutationFn: () =>
      api.post('/api/console/configuration/required-documents', {
        documentTypeId: type.documentTypeId,
        rules: drafts.map((d) => ({
          category: d.category,
          required: d.required,
          stampByDefault: d.stampByDefault,
          note: d.note.trim() || null,
        })),
      }),
    onSuccess: () => {
      toast.success('Saved', `Exporters sending a ${type.name} will see this list.`);
      onSaved();
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : 'Something went wrong.'),
  });

  const used = new Set(drafts.map((d) => d.category));
  const available = categories.filter((c) => !used.has(c.code));
  const labelOf = (code: string) => categories.find((c) => c.code === code)?.label ?? code;

  const patch = (category: string, change: Partial<Draft>) =>
    setDrafts((prev) => prev.map((d) => (d.category === category ? { ...d, ...change } : d)));

  const add = (category: string) =>
    setDrafts((prev) => [...prev, { category, required: true, stampByDefault: false, note: '' }]);

  return (
    <div className="flex flex-col gap-4">
      {/* The picker comes FIRST. Adding a document is what someone opening this is here to do,
          and burying it under a list they have not built yet made it invisible. */}
      <div>
        <p className="[font-size:12.5px] font-medium">Add a document to ask for</p>
        <p className="mt-0.5 [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
          These are your chamber&rsquo;s attachment categories. Add or rename them in{' '}
          <Link className="underline" to="/configuration/master-data">
            master data
          </Link>
          .
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {available.length === 0 ? (
            <p className="[font-size:12.5px] text-[var(--pv-faint)]">
              Every category is already on this list.
            </p>
          ) : (
            available.map((c) => (
              <Button
                key={c.code}
                variant="secondary"
                size="sm"
                leadingIcon={<Plus className="size-3.5" />}
                onClick={() => add(c.code)}
              >
                {c.label}
              </Button>
            ))
          )}
        </div>
      </div>

      {drafts.length === 0 ? (
        <p className="rounded-[var(--pv-radius)] border border-dashed border-[var(--pv-line-strong)] px-4 py-6 text-center [font-size:13px] text-[var(--pv-muted)]">
          Nothing requested for a {type.name} yet. Most chambers ask for a commercial invoice and a
          packing list — add them above.
        </p>
      ) : (
        <ul className="flex list-none flex-col gap-2 p-0">
          {drafts.map((draft) => (
            <li
              key={draft.category}
              className="flex flex-wrap items-start gap-4 rounded-[var(--pv-radius)] border border-[var(--pv-line)] bg-[var(--pv-card)] px-4 py-3"
            >
              <div className="min-w-[180px] flex-1">
                <p className="[font-size:13.5px] font-medium">{labelOf(draft.category)}</p>
                <Input
                  className="mt-2"
                  aria-label={`Why you need a ${labelOf(draft.category)}`}
                  placeholder="Why you need it (optional — the exporter sees this)"
                  value={draft.note}
                  onChange={(e) => patch(draft.category, { note: e.target.value })}
                />
              </div>

              <Checkbox
                checked={draft.required}
                onCheckedChange={(v) => patch(draft.category, { required: v === true })}
                label="Required"
                description="Flagged when missing"
              />
              <Checkbox
                checked={draft.stampByDefault}
                onCheckedChange={(v) => patch(draft.category, { stampByDefault: v === true })}
                label="Normally stamped"
                description="Pre-ticked for the exporter"
              />

              <Button
                variant="ghost"
                size="sm"
                leadingIcon={<Trash2 className="size-3.5" />}
                onClick={() => setDrafts((prev) => prev.filter((d) => d.category !== draft.category))}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          leadingIcon={<Save className="size-3.5" />}
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          Save
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <span className="[font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
          {drafts.length === 0
            ? 'Saving with nothing listed stops this type asking for anything.'
            : `Exporters sending a ${type.name} will see this list before they submit.`}
        </span>
      </div>
    </div>
  );
}
